import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { PageContentPreloader } from 'common/components';
import { isAgreementAutoAcknowledgedBroker } from 'common/utils';

import { getCountries, getKYC } from '../../_redux';
import { KYCForm, TrederoCustomKYCForm, VerificationPanel } from '../../_components';

const KYCPage = ({
  user,
  kycValues,
  kycValuesAreLoaded,
  getKYC,
  countries,
  countriesAreLoaded,
  getCountries,
  states,
  addons,
  statesAreLoaded,
  passportMandatory,
  addressProofMandatory,
  isInWhiteList,
  identificationProofMandatory,
  interfaceConfig,
  dispatch,
}) => {
  const history = useHistory();

  // 存储表单状态
  const [formState, setFormState] = useState({
    pristine: true,
    submitting: false,
    invalid: false,
    onRoundBackButtonClick: () => {},
  });

  // 标记重定向检查是否完成
  const [redirectChecked, setRedirectChecked] = useState(false);

  // 指定 broker（如 10/229）无需签署反向招揽文档，不做强制跳转
  const agreementAutoAcknowledged = isAgreementAutoAcknowledgedBroker(interfaceConfig?.brokerId);

  // 页面加载时自动检查是否需要跳转到personal_page
  useEffect(() => {
    if (user && interfaceConfig && !agreementAutoAcknowledged) {
      const userStatus = user?.status || '';
      const isSolicitationAcknowledge = user?.isSolicitationAcknowledge || false;
      const isAgreementAcknowledged = user?.isAgreementAcknowledged || false;
      const userCountry = user?.nationality || '';
      const whiteListCountriesSetting = interfaceConfig?.whiteListCountriesSetting || {};
      const isWhiteListEnabled = whiteListCountriesSetting.enabled;
      const whiteListCountries = whiteListCountriesSetting.countries || [];
      const isUserInWhiteList = whiteListCountries.includes(userCountry);

      // 检查是否需要跳转的条件：
      // 1. 用户未确认过客户协议
      // 2. 用户状态不是verified时，如果用户未确认过反向招揽协议 且 用户不在白名单国家中（或白名单功能未启用）
      if (
        !isAgreementAcknowledged ||
        (userStatus !== 'verified' && !(isWhiteListEnabled && isUserInWhiteList) && !isSolicitationAcknowledge)
      ) {
        dispatch({
          type: 'SHOW_NOTIFICATION',
          payload: {
            type: 'error',
            message: 'Action Required',
            description: 'Please Complete Required Document Signatures',
          },
        });
        history.push('/personal_page');
        return;
      }
    }
    setRedirectChecked(true);
  }, [user, interfaceConfig, dispatch, history, agreementAutoAcknowledged]);

  // 处理表单状态变化
  const handleFormStateChange = newState => {
    setFormState(prevState => ({ ...prevState, ...newState }));
  };

  useEffect(() => {
    getKYC();
    getCountries();
  }, [getKYC, getCountries]);

  // 重定向检查未完成前不渲染主内容，避免白屏
  if (!redirectChecked) {
    return null;
  }

  const { passportStatus, addressProofStatus, identificationProofStatus } = user || {};

  const passportDisplayed = !passportMandatory || (passportMandatory && passportStatus !== 'notUploaded');
  // 白名单用户无需在KYC阶段强制要求地址证明
  const addressProofDisplayed =
    !addressProofMandatory || isInWhiteList || (addressProofMandatory && addressProofStatus !== 'notUploaded');
  const identificationProofDisplayed =
    !identificationProofMandatory || (identificationProofMandatory && identificationProofStatus !== 'notUploaded');

  const mandatoryDocumentsAreSending = passportDisplayed && addressProofDisplayed && identificationProofDisplayed;

  // 确保 initialValues 中的 address 兼容 post_code 和 postCode
  const normalizeInitialValues = values => {
    if (!values || !values.address) return values;
    const normalized = { ...values };
    if (normalized.address.post_code && !normalized.address.postCode) {
      normalized.address = {
        ...normalized.address,
        postCode: normalized.address.post_code,
      };
    }
    return normalized;
  };

  const initialValues = normalizeInitialValues({ ...kycValues, ...user });

  return (
    <VerificationPanel
      captionId="persInfo"
      warningMessageTextId={user.needsVerification ? 'kycPersonalMessage' : 'kycPersonalMessageDocuments'}
      roundBackButtonProps={{
        onClick: formState.onRoundBackButtonClick,
        disabled: formState.submitting,
      }}>
      {!addons.includes('trederoCustomKyc') && user && (
        <PageContentPreloader ready={kycValuesAreLoaded} type="text">
          <KYCForm
            user={user}
            initialValues={initialValues}
            countries={countries}
            states={states}
            countriesAreLoaded={countriesAreLoaded}
            statesAreLoaded={statesAreLoaded}
            mandatoryDocumentsAreSending={mandatoryDocumentsAreSending}
            onFormStateChange={handleFormStateChange}
          />
        </PageContentPreloader>
      )}
      {addons.includes('trederoCustomKyc') && user && (
        <PageContentPreloader ready={kycValuesAreLoaded} type="text">
          <TrederoCustomKYCForm
            user={user}
            initialValues={initialValues}
            countries={countries}
            states={states}
            countriesAreLoaded={countriesAreLoaded}
            statesAreLoaded={statesAreLoaded}
            mandatoryDocumentsAreSending={mandatoryDocumentsAreSending}
            onFormStateChange={handleFormStateChange}
          />
        </PageContentPreloader>
      )}
    </VerificationPanel>
  );
};

KYCPage.propTypes = {
  getKYC: PropTypes.func.isRequired,
  getCountries: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  kycValues: PropTypes.object.isRequired,
  kycValuesAreLoaded: PropTypes.bool.isRequired,
  countries: PropTypes.array.isRequired,
  countriesAreLoaded: PropTypes.bool.isRequired,
  states: PropTypes.array.isRequired,
  statesAreLoaded: PropTypes.bool.isRequired,
  passportMandatory: PropTypes.bool.isRequired,
  addressProofMandatory: PropTypes.bool.isRequired,
  isInWhiteList: PropTypes.bool.isRequired,
  identificationProofMandatory: PropTypes.bool.isRequired,
  addons: PropTypes.array.isRequired,
  interfaceConfig: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default connect(
  state => ({
    user: state.user.profile,
    kycValues: state.client.kyc.kycValues,
    kycValuesAreLoaded: state.client.kyc.kycValuesAreLoaded,
    countries: state.client.kyc.countries,
    countriesAreLoaded: state.client.kyc.countriesAreLoaded,
    states: state.client.kyc.states,
    statesAreLoaded: state.client.kyc.statesAreLoaded,
    passportMandatory: state.interfaceConfig?.kycSettings?.passport?.mandatory || false,
    addressProofMandatory: state.interfaceConfig?.kycSettings?.address_proof?.mandatory || false,
    isInWhiteList: state.user.profile.isInWhiteList || false,
    identificationProofMandatory: state.interfaceConfig?.kycSettings?.identification_proof?.mandatory || false,
    addons: state.interfaceConfig?.addons || [],
    interfaceConfig: state.interfaceConfig,
  }),
  dispatch => ({
    getKYC: () => dispatch(getKYC.request()),
    getCountries: () => dispatch(getCountries.request()),
    dispatch,
  })
)(KYCPage);
export { KYCPage };
